// New Project modal (task 11.4) — one clean dialog: project name + optional company.
// Typing a new company name creates it on the fly; picking an existing one reuses it.
import React, { useState } from "react";
import { X, FolderPlus, Loader2 } from "lucide-react";
import { useProjects } from "../ProjectContext";

export default function NewProjectModal({ open, onClose }) {
  const { newProject, selectProject } = useProjects();
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [created, setCreated] = useState(null); // {projectId, displayName} after success

  if (!open) return null;

  const close = () => { setName(""); setErr(null); setCreated(null); onClose(); };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true); setErr(null);
    try {
      const c = await newProject(name.trim(), null); // company hidden — backend assigns the default
      setCreated({ projectId: c?.projectId, displayName: c?.displayName || name.trim() });
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Couldn't create the project. Try a different name.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 100001, background: "rgba(8,12,24,0.55)",
      backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px",
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "#fff", borderRadius: "16px", width: "100%", maxWidth: "440px",
        boxShadow: "0 24px 70px rgba(0,0,0,0.32)", overflow: "hidden",
      }}>
        <div style={{ padding: "20px 24px 14px", borderBottom: "1px solid #eef1f6", display: "flex", alignItems: "center", gap: "10px" }}>
          <div style={{ width: "30px", height: "30px", borderRadius: "8px", background: "linear-gradient(135deg,#6366f1,#8b5cf6)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <FolderPlus size={16} color="#fff" />
          </div>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 700, color: "#0f172a" }}>New Project</h3>
            <p style={{ margin: "2px 0 0", fontSize: "12px", color: "#64748b" }}>A project keeps its files and insights separate.</p>
          </div>
          <button onClick={close} style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8" }}><X size={18} /></button>
        </div>

        {created ? (
          <div style={{ padding: "22px 24px 24px", textAlign: "center" }}>
            <div style={{ fontSize: "34px", marginBottom: "8px" }}>✅</div>
            <h4 style={{ margin: "0 0 6px", fontSize: "15px", fontWeight: 700, color: "#0f172a" }}>
              Project “{created.displayName}” created
            </h4>
            <p style={{ margin: "0 0 18px", fontSize: "13px", color: "#64748b", lineHeight: 1.5 }}>
              It starts empty. Switch to it whenever you're ready to upload — you won't lose your current project.
            </p>
            <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
              <button onClick={close} style={{ padding: "10px 16px", borderRadius: "9px", border: "1px solid #e2e8f0", background: "#fff", color: "#475569", cursor: "pointer", fontSize: "14px", fontWeight: 600 }}>Stay here</button>
              {/* selectProject refuses while an upload batch is importing (it's pinned to the
                  project it started in). Only close on a switch that actually happened, so a
                  refusal can't look like it worked. */}
              <button onClick={() => {
                if (created.projectId && selectProject(created.projectId) === false) return;
                close();
              }}
                style={{ padding: "10px 18px", borderRadius: "9px", border: "none", background: "linear-gradient(135deg,#6366f1,#4f46e5)", color: "#fff", cursor: "pointer", fontSize: "14px", fontWeight: 700 }}>
                Switch to it
              </button>
            </div>
          </div>
        ) : (
        <form onSubmit={submit} style={{ padding: "18px 24px 22px", display: "flex", flexDirection: "column", gap: "14px" }}>
          <div>
            <label style={{ fontSize: "12px", fontWeight: 600, color: "#475569", display: "block", marginBottom: "5px" }}>Project name</label>
            <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Q2 Campaign"
              style={{ width: "100%", border: "1px solid #e2e8f0", borderRadius: "9px", padding: "9px 11px", fontSize: "14px", outline: "none", boxSizing: "border-box" }} />
          </div>
          {err && <div style={{ fontSize: "12.5px", color: "#dc2626", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px", padding: "8px 10px" }}>{err}</div>}
          <div style={{ display: "flex", gap: "8px", marginTop: "2px" }}>
            <button type="button" onClick={close} style={{ flex: "0 0 auto", padding: "10px 16px", borderRadius: "9px", border: "1px solid #e2e8f0", background: "#fff", color: "#475569", cursor: "pointer", fontSize: "14px", fontWeight: 600 }}>Cancel</button>
            <button type="submit" disabled={busy || !name.trim()} style={{
              flex: 1, padding: "10px", borderRadius: "9px", border: "none", cursor: busy || !name.trim() ? "not-allowed" : "pointer",
              background: busy || !name.trim() ? "#94a3b8" : "linear-gradient(135deg,#6366f1,#4f46e5)", color: "#fff", fontWeight: 700, fontSize: "14px",
              display: "flex", alignItems: "center", justifyContent: "center", gap: "7px",
            }}>
              {busy ? <Loader2 size={15} className="animate-spin" /> : <FolderPlus size={15} />}
              {busy ? "Creating…" : "Create Project"}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
}
